import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { color, r } from '../../theme/tokens';
import { addCheckerSupplement } from '../../utils/interactionCheckerStore';
import { logAnalyticsEvent } from '../../utils/analytics';
import PickField, { DropdownMessage, Highlight, OptionRow, listboxIdFor } from './PickField';
import {
  SUPPLEMENT_SEARCH_MIN_TERM,
  normalizeForSearch,
  notFoundQueryForAnalytics,
  searchSupplements,
} from './supplementSearch';
import {
  SEARCHING,
  SUPPLEMENTS_HEADING,
  SUPPLEMENTS_INSTRUCTION,
  SUPPLEMENTS_PLACEHOLDER,
  SUPPLEMENT_LIST_FAILED,
  SUPPLEMENT_NO_MATCH,
  noMatchTitle,
} from './copy';

// v5.1's supplements field (STACK-587, states 4–6): the medications field's
// pattern over our supplement list only. Nothing outside the list can be
// added. A search that settles on no match records what was typed, so we
// learn which supplements and aliases to add (Aravind, 2026-10-13; design
// doc §5) — once per distinct text, after typing pauses, not per keystroke,
// and never text that looks like an email or phone number.

const FIELD_ID = 'checker-supplements';
export const NOT_FOUND_SETTLE_MS = 800;

/**
 * @param {Object} props
 * @param {Array} props.supplements - the picks `{ key, name }`
 * @param {{status: string, list: Array}} props.catalog - our list:
 *   status loading | ready | failed
 * @param {boolean} props.atCap
 * @param {boolean} [props.signedIn]
 * @param {Function} props.onRemove - `(key) => void`
 */
export default function SupplementField({ supplements, catalog, atCap, signedIn, onRemove }) {
  const [term, setTerm] = useState('');
  const [highlight, setHighlight] = useState(0);
  const [reported] = useState(() => new Set());

  const picked = useMemo(() => new Set(supplements.map((s) => s.key)), [supplements]);
  const query = term.trim();
  const searching = normalizeForSearch(query).length >= SUPPLEMENT_SEARCH_MIN_TERM;
  const matches = useMemo(
    () => (catalog.status === 'ready' ? searchSupplements(catalog.list, query) : []),
    [catalog, query],
  );
  const available = matches.filter((m) => !picked.has(m.key));
  const noMatch = searching && catalog.status === 'ready' && matches.length === 0;

  useEffect(() => { setHighlight(0); }, [query]);

  useEffect(() => {
    if (!noMatch) return undefined;
    const key = normalizeForSearch(query);
    if (reported.has(key)) return undefined;
    const timer = setTimeout(() => {
      reported.add(key);
      // Never what looks like an email or phone number (hard rule 1).
      const recorded = notFoundQueryForAnalytics(query);
      if (recorded) logAnalyticsEvent('checker_supplement_not_found', { query: recorded });
    }, NOT_FOUND_SETTLE_MS);
    return () => clearTimeout(timer);
  }, [noMatch, query, reported]);

  const pick = (match) => {
    if (!match) return;
    const outcome = addCheckerSupplement(match);
    if (outcome === 'added') {
      logAnalyticsEvent('checker_supplement_added', {
        supplement_key: match.key,
        count: supplements.length + 1,
      });
    }
    if (outcome === 'added' || outcome === 'duplicate') setTerm('');
  };

  const onKeyDown = (e) => {
    if (e.key === 'Escape') {
      setTerm('');
      return;
    }
    if (!available.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => (h + 1) % available.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => (h - 1 + available.length) % available.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      pick(available[highlight]);
    }
  };

  const optionId = (i) => `${FIELD_ID}-option-${i}`;

  const dropdown = () => {
    if (!searching) return null;
    if (catalog.status === 'failed') return <DropdownMessage role="alert">{SUPPLEMENT_LIST_FAILED}</DropdownMessage>;
    if (catalog.status !== 'ready') return <DropdownMessage>{SEARCHING}</DropdownMessage>;
    // Every match already picked reads as no new match, not as "not on our list".
    if (!available.length) {
      return noMatch
        ? <DropdownMessage title={noMatchTitle(query)}>{SUPPLEMENT_NO_MATCH}</DropdownMessage>
        : null;
    }
    return (
      <Box component="ul" role="listbox" id={listboxIdFor(FIELD_ID)} aria-label="Matching supplements" sx={{ listStyle: 'none', m: 0, p: 0 }}>
        {available.map((m, i) => (
          <OptionRow
            key={m.key}
            id={optionId(i)}
            minHeight={48}
            selected={i === highlight}
            onSelect={() => pick(m)}
            onHover={() => setHighlight(i)}
          >
            <Typography component="span" sx={{ fontSize: r(15), color: color.ink, lineHeight: 1.35 }}>
              <Highlight text={m.name} term={query} />
            </Typography>
          </OptionRow>
        ))}
      </Box>
    );
  };

  const content = dropdown();

  return (
    <PickField
      id={FIELD_ID}
      heading={SUPPLEMENTS_HEADING}
      instruction={SUPPLEMENTS_INSTRUCTION}
      placeholder={SUPPLEMENTS_PLACEHOLDER}
      chips={supplements.map((s) => ({ id: s.key, label: s.name }))}
      onRemove={onRemove}
      atCap={atCap}
      signedIn={signedIn}
      term={term}
      onTermChange={setTerm}
      onKeyDown={onKeyDown}
      dropdownOpen={Boolean(content)}
      dropdown={content}
      activeOptionId={available.length ? optionId(highlight) : undefined}
    />
  );
}
